/**
Project: Voice Panel (c)
Title: Full Layout  
Description: Component for hadling the routes of logged-in user
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/
import React, { Component } from "react";
import { Route, Switch } from "react-router-dom";
import { connect } from "react-redux";
import axios from "axios";
import * as microsoftTeams from "@microsoft/teams-js";
import { AuthenticationContext } from "react-adal";
import IdleTimer from "../../core/helpers/idle-timer";
import SessionTimer from "../../core/helpers/session-timer";
import PageLoader from "../pages/common/helpers/page-loader";
import Navbar from "../pages/common/components/navbar";
import IntlUtil from "../../core/helpers/intl-util";
import PageUtil from "../../core/helpers/page-util";
import { AppConstants } from "../../core/settings/app-constants";
import { AppURLProps } from "../../core/settings/app-urls";
import { AppConfigProps } from "../../core/settings/app-config";
import {
  manageError,
  setClientNavigator,
} from "../../core/actions/common-actions";
import {
  hidePageWaitDialog,
  setCountryStates,
  getCountryStates,
  getCommunicationPlatforms,
  setCommunicationPlatforms,
  getAppConsent,
  setUserConsentRecord,
  acceptAppConsent,
} from "../pages/common/actions/general-actions";
import {
  renewIdentitySession,
  isIdentitySessionValid,
} from "../../core/actions/identity-actions";
import {
  setSubscriberIdentity,
  setSubscriberAccount,
  setSubscriberMenus,
} from "../pages/common/actions/subscriber-actions";
import { getAppInsights } from "../../core/settings/azure-insights-config";
import Error404 from "../../core/views/error-404";
import Home from "../pages/common/components/home";
import { msalInstance } from "../../core/settings/msal-config";
import PageWaitDialog from "../pages/common/helpers/page-wait-dailog";
import { authenticateTokenSignin } from "../../core/actions/identity-actions";
import {
  accessLogWithToken,
} from "../../core/actions/common-actions";
import { Text, Dialog } from "@fluentui/react-northstar";
import TokenUtil from "../../core/helpers/token-util";
import _ from "lodash";
import UserConsent from "../pages/common/components/user-consent";
import { VoiceAdminConstants } from "../pages/common/settings/voice-admin-constants";
import TelemetryUtil from "../../core/helpers/telemetry-util";

class FullLayout extends Component {
  _isMounted = false;
  _axiosSource = axios.CancelToken.source();
  _cancelToken = { cancelToken: this._axiosSource.token };
  _intl_ns_common = "common";
  _intl_ns_vpa_common = "vpa_common";
  _azureInsights = getAppInsights();

  constructor(props) {
    super(props);
    this.state = {
      isAppDataFetched: false,
      isSessionTimeoutModalOpen: false,
      isUserConsentModalOpen: false,
      userConsentRecord: null,
      isConsentSubmitted: false,
    };
  }

  setStateAsync = (state) => {
    if (this._isMounted) {
      return new Promise((resolve) => {
        this.setState(state, resolve);
      });
    }
  };

  async componentDidMount() {
    this._isMounted = true;
    PageUtil.scrollToTop();
    await this.loadPageData();
    this._identityIdleTimer = new IdleTimer({
      timeout: process.env.REACT_APP_IDLE_TIMEOUT_SECONDS, //time in seconds
      onTimeout: () => this.signout(),
    });
    this._identitySessionTimer = new SessionTimer({
      timeout: process.env.REACT_APP_SESSION_TIMEOUT_SECONDS, // time in seconds
      onTimeout: () => this.openSessionTimeoutModal(),
    });
  }

  componentWillUnmount() {
    this._isMounted = false;
    this._axiosSource.cancel(
      IntlUtil.getText(
        this._intl_ns_common,
        "notification.warning.requestCancelled"
      )
    );
    if (this._identityIdleTimer) this._identityIdleTimer.cleanUp();
    if (this._identitySessionTimer) this._identitySessionTimer.cleanUp();
  }

  loadPageData = async () => {
    try {
      await this.props.hidePageWaitDialog();
      await this.setStateAsync({ isAppDataFetched: false });
      await this.props.setClientNavigator();
      await this.checkUserSignin();
      await this.props.setSubscriberIdentity();
      await this.props.setSubscriberAccount(this._cancelToken);
      await this.props.setSubscriberMenus(this.props.subscriberAccount);
      await this.fetchCountryList();
      await this.fetchCommunicationPlatforms();
      await this.fetchAppConsent();
      await this.setStateAsync({ isAppDataFetched: true });
    } catch (err) {
      await this.setStateAsync({ isAppDataFetched: true });
      await manageError(err, this.props.history);
    }
  };
  fetchCommunicationPlatforms = async () => {
    await getCommunicationPlatforms()
      .then(async (res) => {
        if (
          res &&
          res.data &&
          res.status === AppConfigProps.httpStatusCode.ok &&
          res.data.records
        ) {
          await this.props.setCommunicationPlatforms(res.data.records);
        } else {
          await this.props.hidePageWaitDialog();
          await manageError(res, this.props.history);
        }
      })
      .catch(async (err) => {
        await this.props.hidePageWaitDialog();
        manageError(err, this.props.history);
      });
  };
  checkIframeWhiteListDomains = async () => {
    if ((window.top === window.self) === false) {
      if (
        _.includes(
          this.props.whitelistDomains,
          document.referrer.replace(/\/+$/, "")
        ) === false &&
        this.props.clientNavigator &&
        this.props.clientNavigator.accessMode.toLowerCase() !==
          AppConstants.CLIENT_NAVIGATOR.ACCESS_MODE_TEAMS_CLIENT.toLowerCase() &&
        this.props.clientNavigator &&
        this.props.clientNavigator.accessMode.toLowerCase() !==
          AppConstants.CLIENT_NAVIGATOR.ACCESS_MODE_TEAMS_BROWSER.toLowerCase()
      ) {
        return this.props.history.push(AppURLProps.ErrorFrame);
      }
    }
  };

  openSessionTimeoutModal = async () => {
    await this.setStateAsync({ isSessionTimeoutModalOpen: true });
  };

  closeSessionTimeoutModal = async () =>
    await this.setStateAsync({ isSessionTimeoutModalOpen: false });

  updateSessionTimeoutStatus = (status) => {
    this.closeSessionTimeoutModal();
    if (status) {
      this._identitySessionTimer.startInterval();
      let isSessionValid = isIdentitySessionValid();
      if (isSessionValid === true) {
        renewIdentitySession(this._cancelToken);
      } else {
        this.signout();
      }
    } else {
      this.signout();
    }
  };

  signout = async () => {
    
    if (this._azureInsights)
      await this._azureInsights.clearAuthenticatedUserContext();
    TokenUtil.removeIdentityToken();
    let account = msalInstance.getActiveAccount();
    await msalInstance.logoutRedirect({
      account: account,
    });
    localStorage.clear();
    return;
  };

  checkUserSignin = () => {
    if (
      this.props.clientNavigator &&
      this.props.clientNavigator.accessMode.toLowerCase() !==
        AppConstants.CLIENT_NAVIGATOR.ACCESS_MODE_REGULAR_BROWSER.toLowerCase()
    ) {
      microsoftTeams.initialize();
      const azureActiveDirectoryAppId =
        process.env.REACT_APP_AZURE_ACTIVE_DIRECTORY_APP_ID;
      microsoftTeams.getContext((ctx) => {
        let loginHint = ctx.loginHint;
        let userId = ctx.loginHint;
        let tenantId = ctx.tid;
        let config = {
          tenant: tenantId,
          clientId: azureActiveDirectoryAppId,
          redirectUri: window.location.origin + AppURLProps.signinSilentEnd, // This should be in the list of redirect uris for the AAD app
          cacheLocation: "localStorage",
          navigateToLoginRequestUrl: false,
          extraQueryParameter:
            "scope=openid+profile&login_hint=" + encodeURIComponent(loginHint),
        };

        let authContext = new AuthenticationContext(config);
        let user = authContext.getCachedUser();
        if (user && user.userName !== userId) {
          // User doesn't match, clear the cache
          authContext.clearCache();
          localStorage.clear();
          this.props.history.push(AppURLProps.signin);
        }
      });
    }
  };

  fetchCountryList = async () => {
    await getCountryStates(this._cancelToken)
      .then(async (res) => {
        if (
          res &&
          res.status === AppConfigProps.httpStatusCode.ok &&
          res.data &&
          res.data.records
        ) {
          await this.props.setCountryStates(res.data.records);
        } else {
          await this.setStateAsync({ isAppDataFetched: true });
          await manageError(res, this.props.history);
        }
      })
      .catch(async (err) => {
        await this.setStateAsync({ isAppDataFetched: true });
        await manageError(err, this.props.history);
      });
  };

  fetchAppConsent = async () => {
    await getAppConsent(this._cancelToken)
      .then(async (res) => {
        if (
          res?.status === AppConfigProps.httpStatusCode.ok &&
          res?.data?.result
        ) {
          let record = res.data.result;

          let isConsentAccepted =
            record.processStatus?.toLowerCase().trim() ===
            VoiceAdminConstants.GENERAL.CONSENT_STATUS.NOT_ACCEPTED?.toLowerCase().trim()
              ? false
              : true;
          await this.props.setUserConsentRecord(record);

          await this.setStateAsync({
            userConsentRecord: record,
            isConsentAccepted: isConsentAccepted,
            isUserConsentModalOpen: isConsentAccepted ? false : true,
          });
        } else {
          await this.props.setUserConsentRecord(null);

          await this.setStateAsync({
            userConsentRecord: null,
            isConsentAccepted: true,
          });
        }
      })
      .catch(async (err) => {
        await this.setStateAsync({
          isConsentAccepted: true,
        });
        TelemetryUtil.logException(err);
        await this.setStateAsync({ isAppDataFetched: true });
      });
  };
  handleInteractiveSignin = async (token, history) => {
    await this.setStateAsync({ isAppDataFetched: false });
    try {
      let res = await authenticateTokenSignin(token);
      if (
        res &&
        res.status === AppConfigProps.httpStatusCode.ok &&
        res.data &&
        res.data.result &&
        res.data.result.accessToken &&
        res.data.result.refreshToken
      ) {
        await TokenUtil.setIdentityToken(
          res.data.result.accessToken,
          res.data.result.refreshToken
        );

        const tokenData = TokenUtil.getIdentitySippioAccessTokenData();
        await this.props.setSubscriberIdentity();
        if (
          tokenData.accountNumber === null ||
          tokenData.accountNumber === ""
        ) {
          accessLogWithToken(
            PageUtil.getPageURL(this.props.history.location),
            "UID-SIF",
            "Sign-in Failure",
            token
          );
          msalInstance.logoutRedirect({
            account: msalInstance.getActiveAccount(),
            onRedirectNavigate: (url) => {
              return false;
            },
          });
          TokenUtil.removeIdentityToken();
          localStorage.clear();
          history.push({
            pathName: AppURLProps.signin,
            state: {
              errorMessage: IntlUtil.getText(
                this._intl_ns_vpa_common,
                "notification.error.noAccountId"
              ),
            },
          });
        } else if (
          tokenData.regionsTemplate === null ||
          tokenData.regionsTemplate === ""
        ) {
          accessLogWithToken(
            PageUtil.getPageURL(this.props.history.location),
            "UID-SIF",
            "Sign-in Failure",
            token
          );
          msalInstance.logoutRedirect({
            account: msalInstance.getActiveAccount(),
            onRedirectNavigate: (url) => {
              return false;
            },
          });
          TokenUtil.removeIdentityToken();
          localStorage.clear();
          history.push({
            pathName: AppURLProps.signin,
            state: {
              errorMessage: IntlUtil.getText(
                this._intl_ns_vpa_common,
                "notification.error.noRegions"
              ),
            },
          });
        } else {
          this.props.history.go(0);
          await accessLogWithToken(
            PageUtil.getPageURL(this.props.history.location),
            "UID-SIS",
            "Sign-in Success",
            res.data.result.accessToken
          );
        }
      } else {
        accessLogWithToken(
          PageUtil.getPageURL(this.props.history.location),
          "UID-SIF",
          "Sign-in Failure",
          token
        );
        msalInstance.logoutRedirect({
          account: msalInstance.getActiveAccount(),
          onRedirectNavigate: (url) => {
            return false;
          },
        });
        TokenUtil.removeIdentityToken();
        localStorage.clear();
        history.push(AppURLProps.signin);
      }
    } catch (err) {
      accessLogWithToken(
        PageUtil.getPageURL(this.props.history.location),
        "UID-SIF",
        "Sign-in Failure",
        token
      );
      await this.setStateAsync({ isAppDataFetched: true });
      msalInstance.logoutRedirect({
        account: msalInstance.getActiveAccount(),
        onRedirectNavigate: (url) => {
          return false;
        },
      });
      TokenUtil.removeIdentityToken();
      localStorage.clear();
      this.props.history.push(AppURLProps.signin);
    }
  };

  renderSessionMessageDialog = () => {
    if (this.state.isSessionTimeoutModalOpen === true) {
      return (
        <Dialog
          backdrop={true}
          cancelButton={IntlUtil.getText(
            this._intl_ns_vpa_common,
            "content.no"
          )}
          confirmButton={IntlUtil.getText(
            this._intl_ns_vpa_common,
            "content.yes"
          )}
          onConfirm={() => {
            this.updateSessionTimeoutStatus(true);
          }}
          onCancel={() => {
            this.updateSessionTimeoutStatus(false);
          }}
          open={this.state.isSessionTimeoutModalOpen}
          content={
            <>
              <Text className="vpa-page-dialog-info-text">
                {IntlUtil.getText(
                  this._intl_ns_vpa_common,
                  "notification.warning.sessionTimeoutMessage1"
                )}
              </Text>{" "}
              <Text className="vpa-page-dialog-info-text">
                {IntlUtil.getText(
                  this._intl_ns_vpa_common,
                  "notification.warning.sessionTimeoutMessage2"
                )}
              </Text>
            </>
          }
          className="vpa-page-dialog"
        />
      );
    }
  };
  handleConsentSubmit = async () => {
    await this.setStateAsync({ isConsentSubmitted: true });
    let tokenData = TokenUtil.getIdentitySippioAccessTokenData();
    let userConsent = {
      consentId: this.state.userConsentRecord.consentId,
      userEmail: tokenData.userEmail,
      accountNumber: tokenData.accountNumber,
      processStatus: VoiceAdminConstants.GENERAL.CONSENT_STATUS.ACCEPTED,
    };
    await acceptAppConsent(userConsent, this._cancelToken)
      .then(async (res) => {
        if (
          res &&
          res.status === AppConfigProps.httpStatusCode.ok &&
          res?.data?.result
        ) {
          if (this.props.userConsentData) {
            await this.props.setUserConsentRecord({
              ...this.props.userConsentData,
              acceptTimestamp: res.data.result?.acceptTimestamp,
              consentUserId: res.data.result?.consentUserId,
            });
          }
          await this.setStateAsync({
            isConsentAccepted: true,
            isConsentSubmitted: false,
          });
        } else {
          await this.setStateAsync({
            isConsentAccepted: true,
            isConsentSubmitted: false,
          });
        }
      })
      .catch(async (err) => {
        if (this.props.userConsentData) {
          await this.props.setUserConsentRecord(null);
        }
        TelemetryUtil.logException(err);

        await this.setStateAsync({
          isConsentAccepted: true,
          isConsentSubmitted: false,
        });
      });
  };
  handleConsentReject = async () => {
    await this.setStateAsync({
      isUserConsentModalOpen: false,
      isAppDataFetched: false,
    });
    await this.signout();
  };
  render() {
    return (
      <div id="vpa-layout">
        <PageLoader
          status={this.state.isAppDataFetched}
          size="medium"
          labelPosition="below"
          label={IntlUtil.getText(
            this._intl_ns_common,
            "content.loadingInprogress"
          )}
          type="full-center"
        >
          <>
            <Navbar
              {...this.props}
              handleInteractiveSignin={this.handleInteractiveSignin}
            />
            <div id="vpa-page">
              {this.state.isConsentAccepted ? (
                <Switch>
                  {" "}
                  <Route path="/" exact component={Home} />
                  {this.props.subscriberMenus
                    ? this.props.subscriberMenus.map((route, index) => {
                        if (route.child && route.child.length > 0) {
                          return route.child.map((route2, index1) => {
                            return (
                              <Route
                                key={`route-${index1}`}
                                path={route2.path}
                                exact={route2.exactPath}
                                component={route2.component}
                              />
                            );
                          });
                        } else {
                          return (
                            <Route
                              key={`route-${index}`}
                              path={route.path}
                              exact={route.exactPath}
                              component={route.component}
                            />
                          );
                        }
                      })
                    : null}
                  <Route path="*" component={Error404} />
                </Switch>
              ) : (
                <UserConsent
                  isUserConsentModalOpen={this.state.isUserConsentModalOpen}
                  isConsentSubmitted={this.state.isConsentSubmitted}
                  handleConsentAccept={this.handleConsentSubmit}
                  handleConsentReject={this.handleConsentReject}
                  userConsentRecord={this.state.userConsentRecord}
                />
              )}
            </div>
            <PageWaitDialog />
            {this.renderSessionMessageDialog()}
          </>
        </PageLoader>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  appName: state.appSettings.appName,
  clientNavigator: state.appSettings.clientNavigator,
  subscriberAccount: state.subscriberStore.subscriberAccount,
  subscriberMenus: state.subscriberStore.subscriberMenus,
  countryStatesData: state.generalStore.countryStatesData,
  whitelistDomains: state.appSettings.whitelistDomains,
  userConsentData: state.generalStore.userConsentData,
});
const mapActionToProps = {
  setClientNavigator,
  setSubscriberIdentity,
  setSubscriberAccount,
  setSubscriberMenus,
  setCountryStates,
  hidePageWaitDialog,
  setCommunicationPlatforms,
  setUserConsentRecord,
};

export default connect(mapStateToProps, mapActionToProps)(FullLayout);
